import React, { useEffect, useRef } from "react";
import { getShortLang } from "../libs/detectLanguage";

type Props = {
	appId: string;
	onLogin: Function;
};

type CredentialResponse = { credential?: string };

// Google Identity Services is loaded in app.tsx via loadScript("https://accounts.google.com/gsi/client")
const getGsi = (): any => (window as any).google?.accounts?.id;

const MAX_ATTEMPTS = 50; // 50 * 100ms = 5s

/**
 * Renders Google's official "Sign in with Google" button.
 *
 * The button uses a popup on accounts.google.com, which works without third-party storage
 * (Brave, Safari), inside the cross-origin widget iframe, and offers "Use another account".
 * The former One Tap flow (google.accounts.id.prompt) did none of these.
 */
export default function GoogleLogin(props: Props) {
	const container = useRef<HTMLDivElement>(null);
	const onLoginRef = useRef(props.onLogin);
	onLoginRef.current = props.onLogin;

	useEffect(() => {
		let cancelled = false;
		let attempts = 0;

		const render = () => {
			if (cancelled) return;

			const gsi = getGsi();
			if (!gsi) {
				if (++attempts < MAX_ATTEMPTS) {
					setTimeout(render, 100);
				} else {
					console.warn("Google Identity Services did not load, Google login is unavailable");
				}
				return;
			}

			if (!container.current) return;

			gsi.initialize({
				client_id: props.appId || process.env.REACT_APP_GOOGLE_CLIENT_ID,
				ux_mode: "popup",
				use_fedcm_for_button: true,
				itp_support: true,
				callback: (response: CredentialResponse) => {
					if (response && response.credential) {
						onLoginRef.current(response.credential);
					}
				},
			});

			gsi.renderButton(container.current, {
				type: "standard",
				theme: "outline",
				size: "medium",
				shape: "pill",
				text: "signin_with",
				locale: getShortLang(),
			});
		};

		render();

		return () => {
			cancelled = true;
		};
	}, [props.appId]);

	return (
		<div
			ref={container}
			className="googleLoginButton"
			style={{ display: "inline-block", verticalAlign: "middle" }}
		/>
	);
}
